import { nine } from '../core/Config.js';
import dayjs from "dayjs";
import React from 'react'; // 🎯 React 내장 헬퍼 임포트
import { createRoot } from 'react-dom/client';
import { NinePopup } from './NinePopup.js';
import { TreeUtil } from './TreeUtil.js'; // 분리한 파일 임포트
import { NineSimplePopup } from '../ux/dialog/nine-simple-popup.js';

export class NineUtil {

	// 📅 1. 날짜 포맷 헬퍼 (인스턴스 메서드)
	formatDate = (v, fmt) => {
		return dayjs(v).format(fmt);
	};

	/**
	 * 🔮 초간단 만능 드래그 팝업 엔진 (Promise/Then 규격)
	 * @param {React.Component} ContentComponent - 팝업 내부에 띄울 리액트 컴포넌트
	 * @param {Object} param - 팝업 알맹이에 'data'라는 이름으로 배달될 순수 데이터 객체
	 * @param {string} title - 십자 드래그 바 헤더에 꽂힐 타이틀명 (기본값: Details)
	 * @returns {Promise} 팝업이 닫힐 때 결과 데이터를 반환하는 프로퍼티 영수증
	 */
	popup = (ContentComponent, param = {}, title = "Details") => {
		document.querySelectorAll(NinePopup.tagName).forEach(el => el.remove());

		const $popupWrapper = document.createElement(NinePopup.tagName);
		document.body.appendChild($popupWrapper);

		return new Promise((resolve) => {
			const $bodySpace = $popupWrapper.popupBody;
			const $dialogComp = $popupWrapper.dialog;

			if (title && $dialogComp) $dialogComp.setAttribute("subject", title);

			const root = createRoot($bodySpace);

			// 🎯 오직 하나, 다이얼로그가 완전히 파괴(Closed)되는 시점만 딱 낚아챕니다.
			$dialogComp?.addEventListener('closed', (e) => {
				resolve(e.detail); // 최종 반환값 부모의 .then()으로 전달

				setTimeout(() => {
					root.unmount();
					$popupWrapper.remove();
				}, 0);
			});

			root.render(React.createElement(ContentComponent, { data: param }));

			setTimeout(() => {
				$dialogComp?.showModal();
			}, 0);
		});
	};

	/**
	 * 🎯 Non-Shadow DOM 기반의 자유로운 팝업 오픈 메서드
	 */
	simplePopup = (ContentComponent, param = {}, title = "Details") => {
		document.querySelectorAll(NineSimplePopup.tagName).forEach(el => el.remove());

		const $popupWrapper = document.createElement(NineSimplePopup.tagName);
		if (title) $popupWrapper.setAttribute("subject", title);
		document.body.appendChild($popupWrapper);

		return new Promise((resolve) => {
			const $bodySpace = $popupWrapper.popupBody;
			const root = createRoot($bodySpace);

			$popupWrapper.addEventListener('closed', (e) => {
				resolve(e.detail);
				setTimeout(() => {
					root.unmount();
					$popupWrapper.remove();
				}, 0);
			});

			root.render(React.createElement(ContentComponent, {
				data: param,
				onClose: (result) => $popupWrapper.close(result)
			}));
		});
	};

	/**
	 * 🎯 2. Shadow DOM 경계를 무력화하고 깊이 상관없이 모든 요소를 수집하는 만능 쿼리
	 * @param {string} selector - 찾고자 하는 CSS 선택자 (예: 'input[name]')
	 * @param {HTMLElement|ShadowRoot|Document} root - 탐색 시작점
	 * @returns {HTMLElement[]} 매칭된 자바스크립트 DOM 엘리먼트 배열
	 */
	querySelectorAll = (selector, root = document) => {
		const results = [];

		const search = (node) => {
			if (!node) return;

			// 현재 노드가 ShadowRoot를 가지고 있다면 그 내부부터 탐색
			if (node.shadowRoot) {
				search(node.shadowRoot);
			}

			// 현재 레벨(Light DOM 또는 Shadow DOM)에서 매칭되는 타겟 수집
			node.querySelectorAll?.(selector).forEach(elem => {
				if (!results.includes(elem)) {
					results.push(elem);
				}
			});

			// 성능 최적화: querySelectorAll('*') 대신 실제 자식 엘리먼트들만 루프 돌며 재귀 탐색
			const children = node.children ? Array.from(node.children) : [];
			children.forEach(child => {
				search(child);
			});
		};

		search(root);
		return results;
	};

	/**
	 * 🎯 3. Shadow DOM 경계를 뚫고 가장 먼저 매칭되는 단 하나의 요소를 반환 (조기 종료 최적화)
	 * @param {string} selector - 찾고자 하는 CSS 선택자
	 * @param {HTMLElement|ShadowRoot|Document} root - 탐색 시작점
	 * @returns {HTMLElement|null} 매칭된 엘리먼트 객체 또는 null
	 */
	querySelector = (selector, root = document) => {
		if (!root) return null;

		// 현재 노드 자체에서 바로 매칭되는 게 있다면 즉시 반환
		let found = root.querySelector?.(selector);
		if (found) return found;

		// 현재 노드에 Shadow DOM 장벽이 있다면 그 내부를 먼저 탐색
		if (root.shadowRoot) {
			found = this.querySelector(selector, root.shadowRoot);
			if (found) return found;
		}

		// 자식 요소들을 순회하며 깊숙이 숨은 타겟 추적
		const children = root.children ? Array.from(root.children) : [];
		for (const child of children) {
			found = this.querySelector(selector, child);
			if (found) return found;
		}

		return null;
	};

	/**
	 * 🎯 4. Shadow DOM 경계를 관통하여 가장 가까운 조상 요소를 찾는 만능 클로지스트
	 * @param {string} selector - 찾고자 하는 부모의 CSS 선택자 (예: 'nine-popup')
	 * @param {HTMLElement} startElement - 탐색을 시작할 기준 엘리먼트 (보통 e.target)
	 * @returns {HTMLElement|null} 매칭된 조상 엘리먼트 객체 또는 null
	 */
	closest = (selector, startElement) => {
		if (!startElement || typeof selector !== 'string') return null;

		let currentElement = startElement;

		while (currentElement) {
			// ① 일반 Element 노드일 때만 matches 검사 (ShadowRoot 등 분기 방어)
			if (currentElement.nodeType === Node.ELEMENT_NODE) {
				if (currentElement.matches(selector)) {
					return currentElement;
				}
			}

			// ② 🚀 [핵심] 현재 노드가 Shadow DOM 내부라면 getRootNode().host를 통해 바깥 세상(Host)으로 탈출!
			// 만약 더 이상 올라갈 Host가 없다면 일반 상위 부모(parentElement)로 이동
			const rootNode = currentElement.getRootNode?.();

			if (rootNode && rootNode instanceof ShadowRoot) {
				currentElement = rootNode.host; // 장벽 돌파
			} else {
				currentElement = currentElement.parentElement || null; // 일반 부모 추적
			}
		}

		return null;
	};

	/**
	login = (provider) => {
		window.location.href = `${window.__API_BASE_URL__ || ""}/accounts/auth/${provider}/login`;
	}; */
	login = (provider) => {
		const currentFrontendUrl = window.location.origin; // 로컬이면 http://localhost:8102, 운영이면 실제 도메인
		const apiBase = window.__API_BASE_URL__ || "";

		window.location.href = `${apiBase}/accounts/auth/${provider}/login?frontendUrl=${encodeURIComponent(currentFrontendUrl)}`;
	};
	#requestLogin = (provider) => {
		const currentFrontendUrl = window.location.origin;
		const apiBase = window.__API_BASE_URL__ || "";

		window.location.href = `${apiBase}/accounts/auth/${provider}/login?frontendUrl=${encodeURIComponent(currentFrontendUrl)}`;
	};

	// 🎯 provider별 메서드 체인 구성
	google = () => ({
		login: () => this.#requestLogin('google')
	});

	kakao = () => ({
		login: () => this.#requestLogin('kakao')
	});

	naver = () => ({
		login: () => this.#requestLogin('naver')
	});

	randomUUID = (num) => {
		if (!num) num = 10;

		const characters ='ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
		let result = '';
		const charactersLength = characters.length;
		for (let i = 0; i < num; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}

		return result;
	};

	tree = {
		sort: TreeUtil.sort,
	};
}

const utilInstance = new NineUtil();
// 🎯 [핵심] 글로벌 `nine` 객체에 유틸리티 메서드들을 통째로 이식합니다.
Object.assign(nine, utilInstance);